import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ScrollReveal from "../components/ScrollReveal";
import SmoothScroll from "../components/SmoothScroll";
import SimpleRotatingText from "../components/SimpleRotatingText";
import VideoModal from "../components/VideoModal";
import { getCopy, getCopyObject } from "../utils/copyUtils";

// Define types for the FAQ questions
interface FAQQuestion {
  question: string;
  answer: string;
}

// Define types for the feature items
interface FeatureItem {
  title: string;
  icon: string;
}

const HomePage: React.FC = () => {
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Get copy objects
  const hero = getCopyObject("home.hero");
  const agenticSecurity = getCopyObject("home.agentic_security");
  const howItWorks = getCopyObject("home.how_it_works");
  const integrations = getCopyObject("home.integrations");
  const faq = getCopyObject<{ title: string; text: string; questions: FAQQuestion[] }>("home.faq");
  const features = getCopyObject<FeatureItem[]>("home.features.items");

  const openVideoModal = () => {
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
  };

  useEffect(() => {
    // Initialize lazy video loading
    const lazyVideos = document.querySelectorAll(".lazy-video");

    if ("IntersectionObserver" in window) {
      const lazyVideoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const lazyVideo = entry.target as HTMLVideoElement;
            const src = lazyVideo.dataset.src;
            if (src) {
              lazyVideo.src = src;
              lazyVideo.load();
              lazyVideoObserver.unobserve(lazyVideo);
            }
          }
        });
      });

      lazyVideos.forEach((lazyVideo) => {
        lazyVideoObserver.observe(lazyVideo);
      });
    }

    // Agentic video play functionality
    const agenticVideo = document.querySelector(".agentic-video");
    const playButton = document.querySelector(".agentic-play-button");

    if (agenticVideo && playButton) {
      playButton.addEventListener("click", () => {
        const video = agenticVideo.querySelector("video") as HTMLVideoElement;
        if (video) {
          if (video.paused) {
            video.play();
            agenticVideo.classList.add("playing");
          } else {
            video.pause();
            agenticVideo.classList.remove("playing");
          }
        }
      });
    }

    return () => {
      // Cleanup if needed
      const agenticVideo = document.querySelector(".agentic-video");
      const playButton = document.querySelector(".agentic-play-button");
      if (agenticVideo && playButton) {
        playButton.removeEventListener("click", () => {});
      }
    };
  }, []);

  const toggleAccordion = (index: number) => {
    if (activeAccordion === index) {
      setActiveAccordion(null);
    } else {
      setActiveAccordion(index);
    }
  };

  return (
    <>
      <SmoothScroll />

      {/* Video Modal */}
      <VideoModal isOpen={isVideoModalOpen} onClose={closeVideoModal} videoId="1081420092" />

      {/* Hero Section */}
      <section className="hero hero_main hero_bg-dark">
        <div className="hero__particles-video">
          <video autoPlay muted loop playsInline src="/images/hero/particles.mp4"></video>
        </div>
        <div className="container">
          <div className="hero__orbit-background">
            <div className="hero__orbit-background-wrapper">
              <div className="hero__orbit-background-circle"></div>
              <div className="hero__orbit-background-circle"></div>
            </div>
          </div>

          <ScrollReveal className="hero-content" direction="up" delay={200}>
            <h1 className="hero-title">
              <SimpleRotatingText prefixText={hero?.title_prefix} texts={hero?.title_rotating_texts || []} />
            </h1>
            <p className="hero-description">{hero?.description}</p>
            <div className="hero-cta">
              <Link to="/book-demo" className="btn btn-primary">
                {hero?.cta_primary}
              </Link>
              <button className="hero-cta__watch-demo btn btn-outline" onClick={openVideoModal}>
                <img src="/images/play-filled-icon.svg" alt="play icon" />
                {hero?.cta_secondary}
              </button>
            </div>
            <div className="trusted-by">
              <p>{hero?.trusted_text}</p>
              <div className="trusted-logos">
                <img src="/images/logos/Forbes.png" alt="Forbes" />
                <img src="/images/logos/TechRadar.png" alt="TechRadar" />
                <img src="/images/logos/CNN.png" alt="CNN" />
                <img src="/images/logos/TechCrunch.png" alt="TechCrunch" />
                <img src="/images/logos/Gizmodo.png" alt="Gizmodo" />
              </div>
            </div>
          </ScrollReveal>
        </div>
        <img className="hero-blub-bg" src="/images/features/blub.png" alt="blub" />
      </section>

      <main className="main">
        <div className="main__curved curved-ellipse"></div>
        <div className="main__curved-line curved-ellipse"></div>

        {/* Features Section */}
        <section className="features">
          <img className="features__circles-bg" src="/images/features/circles.svg" alt="circles" />

          <div className="container">
            <div className="features__image resonance-image glass-border-wrapper">
              <video
                autoPlay
                playsInline
                loop
                muted
                preload="auto"
                poster="/images/dashboard-resonance.png"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              >
                <source src="/videos/resonance.mp4" type="video/mp4" />
              </video>
            </div>

            <div className="features__rows">
              <ScrollReveal className="features__row features__row-first" direction="left" delay={300}>
                {features && features.length >= 2 && (
                  <>
                    <div className="features__feature">
                      <div className="features__feature-icon">
                        <img src={features[0].icon} alt={features[0].title} />
                      </div>
                      <div className="features__feature-text">{features[0].title}</div>
                    </div>

                    <div className="features__feature">
                      <div className="features__feature-icon">
                        <img src={features[1].icon} alt={features[1].title} />
                      </div>
                      <div className="features__feature-text">{features[1].title}</div>
                    </div>
                  </>
                )}
              </ScrollReveal>

              <ScrollReveal className="features__row features__row-second" direction="left" delay={500}>
                {features && features.length >= 4 && (
                  <>
                    <div className="features__feature">
                      <div className="features__feature-icon">
                        <img src={features[2].icon} alt={features[2].title} />
                      </div>
                      <div className="features__feature-text">{features[2].title}</div>
                    </div>

                    <div className="features__feature">
                      <div className="features__feature-icon">
                        <img src={features[3].icon} alt={features[3].title} />
                      </div>
                      <div className="features__feature-text">{features[3].title}</div>
                    </div>
                  </>
                )}
              </ScrollReveal>

              <ScrollReveal className="features__row features__row-third" direction="left" delay={700}>
                {features && features.length >= 6 && (
                  <>
                    <div className="features__feature">
                      <div className="features__feature-icon">
                        <img src={features[4].icon} alt={features[4].title} />
                      </div>
                      <div className="features__feature-text">{features[4].title}</div>
                    </div>

                    <div className="features__feature">
                      <div className="features__feature-icon">
                        <img src={features[5].icon} alt={features[5].title} />
                      </div>
                      <div className="features__feature-text">{features[5].title}</div>
                    </div>
                  </>
                )}
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Agentic Security Section */}
        <section className="agentic-security" id="agentic-security">
          <div className="agentic-container container">
            <ScrollReveal className="agentic-video glass-border-wrapper" direction="right" delay={200}>
              <video
                className="lazy-video"
                autoPlay
                playsInline
                loop
                muted
                preload="auto"
                poster="/images/agentic/agentic-main.png"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              >
                <source src="/videos/soc_agent.mp4" type="video/mp4" />
              </video>
            </ScrollReveal>
            <ScrollReveal className="agentic-content" direction="left" delay={300}>
              <div className="agentic-header">
                <span className="new-badge">{agenticSecurity?.new_badge}</span>
                <h2>{agenticSecurity?.title}</h2>
              </div>
              <p className="agentic-subtitle">{agenticSecurity?.subtitle}</p>
              <p className="agentic-description">{agenticSecurity?.description}</p>
              <Link to="/solutions/autonomous-soc" className="btn btn-primary">
                {agenticSecurity?.cta}
              </Link>
            </ScrollReveal>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="how-it-works">
          <div className="how-it-works__curved curved-ellipse"></div>
          <div className="how-it-works__curved-line curved-ellipse"></div>
          <div className="how-it-works-container">
            <ScrollReveal direction="up" delay={200}>
              <h2 className="how-it-works-title">{howItWorks?.title}</h2>
              <p className="how-it-works-subtitle">{howItWorks?.subtitle}</p>
            </ScrollReveal>

            <div className="how-it-works__steps" style={{ position: "relative", minHeight: "800px", width: "100%" }}>
              <ScrollReveal className="how-it-works__step" direction="up" delay={400}>
                <div className="how-it-works__step-header">
                  <div className="how-it-works__step-number">1</div>
                  <p className="how-it-works__step-title">{howItWorks?.step1_title}</p>
                </div>
                <div className="how-it-works-search">
                  <span className="how-it-works-search-text">{howItWorks?.step1_placeholder}</span>
                  <div className="how-it-works-search-icon">
                    <img src="/images/how-it-works/search-icon.svg" alt="Search" />
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal className="how-it-works__step" direction="up" delay={600}>
                <div className="how-it-works__step-header">
                  <div className="how-it-works__step-number">2</div>
                  <p className="how-it-works__step-title">{howItWorks?.step2_title}</p>
                </div>
                <div className="how-it-works__image resonance-image glass-border-wrapper">
                  <video
                    autoPlay
                    playsInline
                    loop
                    muted
                    preload="auto"
                    poster="/images/dashboard-resonance.png"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  >
                    <source src="/videos/resonance.mp4" type="video/mp4" />
                  </video>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Integrations Section */}
        <section className="integrations">
          <div className="integrations__curved curved-ellipse"></div>
          <div className="integrations__curved-line curved-ellipse"></div>
          <div className="integrations-container container">
            <ScrollReveal className="integrations-content" direction="left" delay={200}>
              <h2 className="integrations-title">{integrations?.title}</h2>
              <p className="integrations-text">{integrations?.text}</p>
              <button className="btn btn-primary">{integrations?.cta}</button>
            </ScrollReveal>
            <ScrollReveal className="integrations-logos" direction="right" delay={400}>
              <div className="integrations-logo-row">
                <div className="integration-card">
                  <img src="/images/integrations/jira.svg" alt="Jira" />
                  <span>JIRA</span>
                </div>
                <div className="integration-card">
                  <img src="/images/integrations/azure.svg" alt="Azure" />
                  <span>Azure</span>
                </div>
                <div className="integration-card">
                  <img src="/images/integrations/slack.svg" alt="Slack" />
                  <span>Slack</span>
                </div>
              </div>
              <div className="integrations-logo-row">
                <div className="integration-card">
                  <img src="/images/integrations/ticket.svg" alt="Ticket" />
                  <span>Ticket</span>
                </div>
                <div className="integration-card">
                  <img src="/images/integrations/microsoft-teams.svg" alt="Microsoft Teams" />
                  <span>Microsoft Teams</span>
                </div>
                <div className="integration-card">
                  <img src="/images/integrations/service-now.svg" alt="Service Now" />
                  <span>Service Now</span>
                </div>
              </div>
              <div className="integrations-logo-row">
                <div className="integration-card">
                  <img src="/images/integrations/team1.svg" alt="OneLogin" />
                  <span>OneLogin</span>
                </div>
                <div className="integration-card">
                  <img src="/images/integrations/telegram.svg" alt="Telegram" />
                  <span>Telegram</span>
                </div>
                <div className="integration-card">
                  <img src="/images/integrations/google-cloud.svg" alt="Google Cloud" />
                  <span>Google Cloud</span>
                </div>
              </div>
              <div className="integrations-logo-row">
                <span className="integrations-more">{integrations?.more_text}</span>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq">
          <div className="container">
            <ScrollReveal direction="up" delay={200}>
              <h2 className="faq-title">{faq?.title}</h2>
              <p className="faq-text">{faq?.text}</p>
            </ScrollReveal>

            <ScrollReveal className="accordion" direction="up" delay={300}>
              {faq?.questions?.map((item: FAQQuestion, index: number) => (
                <div key={index} className={`accordion-item ${activeAccordion === index ? "active" : ""}`}>
                  <div className="accordion-header" onClick={() => toggleAccordion(index)}>
                    <h3 className="accordion-title">{item.question}</h3>
                    <span className="accordion-icon"></span>
                  </div>
                  <div className="accordion-content">
                    <p>{item.answer}</p>
                  </div>
                </div>
              ))}
            </ScrollReveal>
          </div>
        </section>
      </main>
    </>
  );
};

export default HomePage;
